import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Phone, Copy, Send, Linkedin, Instagram, CalendarCheck, ExternalLink, MessageCircle } from "lucide-react";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const socials = [
  { label: "LinkedIn", href: "https://www.linkedin.com/in/yuriselvaggio/", icon: Linkedin, testid: "social-linkedin" },
  { label: "Instagram", href: "https://www.instagram.com/yuri.selvaggio/", icon: Instagram, testid: "social-instagram" },
];

export const Contatti = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const copy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} ${t.contatti.copied}`);
    } catch {
      toast.error(t.contatti.copyErr);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contatti.toastOk);
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error(t.contatti.toastErr);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contatti" className="relative py-24 sm:py-32 bg-white/60 border-t border-[#DCEEF5]" data-testid="contatti-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="font-mono-ed text-xs uppercase tracking-[0.22em] text-[#0284C7]">{t.contatti.label}</span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#0B2530] mt-4 leading-tight">
            {t.contatti.h2a}<em className="font-serif-ed italic font-medium text-[#06B6D4]">{t.contatti.h2em}</em>
          </h2>
          <p className="text-[#3D5A66] mt-6 text-base sm:text-lg leading-relaxed max-w-md">
            {t.contatti.intro}
          </p>

          <div className="mt-10 space-y-4">
            <button onClick={() => copy("info@yurjselvaggio.it", "Email")} data-testid="contact-email-card"
              className="w-full flex items-center justify-between rounded-2xl border border-[#DCEEF5] bg-white px-6 py-5 hover:border-[#7DD3FC] hover:-translate-y-0.5 transition-all duration-300 group text-left">
              <span className="flex items-center gap-4">
                <Mail size={20} className="text-[#06B6D4]" />
                <span>
                  <span className="block text-xs font-mono-ed uppercase tracking-[0.16em] text-[#5F7E8A]">{t.contatti.emailLabel}</span>
                  <span className="block font-semibold text-[#0B2530]">info@yurjselvaggio.it</span>
                </span>
              </span>
              <Copy size={16} className="text-[#94B7C4] group-hover:text-[#0284C7] transition-colors" />
            </button>
            <button onClick={() => copy("+355 0694524787", t.contatti.phoneLabel)} data-testid="contact-phone-card"
              className="w-full flex items-center justify-between rounded-2xl border border-[#DCEEF5] bg-white px-6 py-5 hover:border-[#7DD3FC] hover:-translate-y-0.5 transition-all duration-300 group text-left">
              <span className="flex items-center gap-4">
                <Phone size={20} className="text-[#06B6D4]" />
                <span>
                  <span className="block text-xs font-mono-ed uppercase tracking-[0.16em] text-[#5F7E8A]">{t.contatti.phoneLabel}</span>
                  <span className="block font-semibold text-[#0B2530]">+355 (0) 694524787</span>
                </span>
              </span>
              <Copy size={16} className="text-[#94B7C4] group-hover:text-[#0284C7] transition-colors" />
            </button>
            <a href="https://wa.me/355694524787?text=Ciao%20Yuri%2C%20ti%20contatto%20dal%20tuo%20sito" target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-card"
              className="w-full flex items-center justify-between rounded-2xl border border-[#BBF7D0] bg-[#F0FDF4] px-6 py-5 hover:border-[#25D366] hover:-translate-y-0.5 transition-all duration-300 group">
              <span className="flex items-center gap-4">
                <MessageCircle size={20} className="text-[#25D366]" />
                <span>
                  <span className="block text-xs font-mono-ed uppercase tracking-[0.16em] text-[#15803D]">WhatsApp</span>
                  <span className="block font-semibold text-[#0B2530]">+355 694 524 787</span>
                </span>
              </span>
              <ExternalLink size={16} className="text-[#86EFAC] group-hover:text-[#25D366] transition-colors" />
            </a>
          </div>

          <div className="flex gap-3 mt-8">
            {socials.map((s) => (
              <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" data-testid={s.testid} aria-label={s.label}
                className="w-12 h-12 rounded-full border border-[#DCEEF5] bg-white flex items-center justify-center text-[#3D5A66] hover:bg-[#0284C7] hover:text-white hover:border-[#0284C7] transition-colors duration-300 active:scale-95">
                <s.icon size={18} />
              </a>
            ))}
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-3xl bg-white border border-[#DCEEF5] p-8 sm:p-10 shadow-xl shadow-sky-500/5"
          data-testid="contact-form"
        >
          <h3 className="font-display text-xl font-bold text-[#0B2530]">{t.contatti.formTitle}</h3>
          <div className="mt-6 space-y-5">
            <div>
              <label htmlFor="cf-name" className="block text-xs font-mono-ed uppercase tracking-[0.16em] text-[#5F7E8A] mb-2">{t.contatti.name}</label>
              <input id="cf-name" required value={form.name} data-testid="contact-form-name"
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full rounded-xl border border-[#DCEEF5] bg-[#F8FBFD] px-4 py-3 text-sm text-[#0B2530] outline-none focus:border-[#06B6D4] focus:ring-2 focus:ring-cyan-100 transition"
                placeholder={t.contatti.phName} />
            </div>
            <div>
              <label htmlFor="cf-email" className="block text-xs font-mono-ed uppercase tracking-[0.16em] text-[#5F7E8A] mb-2">{t.contatti.email}</label>
              <input id="cf-email" type="email" required value={form.email} data-testid="contact-form-email"
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full rounded-xl border border-[#DCEEF5] bg-[#F8FBFD] px-4 py-3 text-sm text-[#0B2530] outline-none focus:border-[#06B6D4] focus:ring-2 focus:ring-cyan-100 transition"
                placeholder={t.contatti.phEmail} />
            </div>
            <div>
              <label htmlFor="cf-message" className="block text-xs font-mono-ed uppercase tracking-[0.16em] text-[#5F7E8A] mb-2">{t.contatti.message}</label>
              <textarea id="cf-message" required rows={5} value={form.message} data-testid="contact-form-message"
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full rounded-xl border border-[#DCEEF5] bg-[#F8FBFD] px-4 py-3 text-sm text-[#0B2530] outline-none focus:border-[#06B6D4] focus:ring-2 focus:ring-cyan-100 transition resize-none"
                placeholder={t.contatti.phMsg} />
            </div>
            <button type="submit" disabled={sending} data-testid="contact-form-submit"
              className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0284C7] text-white font-semibold px-7 py-3.5 hover:bg-[#075E8A] transition-colors duration-300 active:scale-95 disabled:opacity-60">
              {sending ? t.contatti.sending : t.contatti.submit}
              <Send size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </div>
        </motion.form>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-70px" }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-7xl mx-auto px-5 sm:px-8 mt-14"
        data-testid="contatti-agenda-section"
      >
        <div className="rounded-3xl bg-gradient-to-br from-[#ECFEFF] to-[#F0F9FF] border border-[#BAE6FD] p-8 sm:p-12 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-white border border-[#A5F3FC] px-4 py-1.5 text-xs font-mono-ed uppercase tracking-[0.14em] text-[#0369A1]">
              <CalendarCheck size={13} /> {t.contatti.agendaLabel}
            </span>
            <h3 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B2530] mt-5">
              {t.contatti.agendaTitle}
            </h3>
            <p className="font-serif-ed text-lg sm:text-xl text-[#3D5A66] leading-relaxed mt-4">
              {t.contatti.agendaText}
            </p>
          </div>
          <div className="text-center md:text-right">
            <a href="https://calendar.app.google/wxxQ7pGiPAyqhz3K7" target="_blank" rel="noopener noreferrer" data-testid="contatti-booking-button"
              className="group inline-flex items-center gap-2 rounded-full bg-[#0284C7] text-white font-semibold px-8 py-4 hover:bg-[#075E8A] transition-colors duration-300 active:scale-95 shadow-lg shadow-sky-500/20">
              {t.contatti.agendaBtn}
              <ExternalLink size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <p className="text-xs text-[#5F7E8A] mt-4 max-w-xs mx-auto md:ml-auto md:mr-0">
              {t.contatti.agendaNote}
            </p>
          </div>
        </div>
      </motion.div>
    </section>
  );
};
